export interface PasswordRequirement {
  id: string;
  label: string;
  test: (password: string) => boolean;
  description: string;
}

export interface PasswordStrength {
  score: number; // 0-4
  label: string;
  color: string;
  requirements: PasswordRequirement[];
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: "minLength",
    label: "At least 8 characters",
    test: (password: string) => password.length >= 8,
    description: "Password must be at least 8 characters long",
  },
  {
    id: "hasUppercase",
    label: "One uppercase letter (A-Z)",
    test: (password: string) => /[A-Z]/.test(password),
    description: "Password must contain at least one uppercase letter",
  },
  {
    id: "hasLowercase",
    label: "One lowercase letter (a-z)",
    test: (password: string) => /[a-z]/.test(password),
    description: "Password must contain at least one lowercase letter",
  },
  {
    id: "hasNumber",
    label: "One number (0-9)",
    test: (password: string) => /\d/.test(password),
    description: "Password must contain at least one number",
  },
  {
    id: "hasSpecialChar",
    label: "One special character (!@#$%^&*)",
    test: (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
    description: "Password must contain at least one special character",
  },
  {
    id: "noCommonPatterns",
    label: "No common patterns",
    test: (password: string) => {
      const commonPatterns = [
        /123456/,
        /password/i,
        /qwerty/i,
        /abc123/i,
        /admin/i,
        /letmein/i,
        /welcome/i,
        /monkey/i,
        /dragon/i,
      ];
      return !commonPatterns.some((pattern) => pattern.test(password));
    },
    description: "Password must not contain common patterns or words",
  },
];

export const validatePassword = (password: string): PasswordStrength => {
  if (!password) {
    return {
      score: 0,
      label: "Very Weak",
      color: "text-red-500",
      requirements: PASSWORD_REQUIREMENTS,
    };
  }

  const passedRequirements = PASSWORD_REQUIREMENTS.filter((req) =>
    req.test(password)
  );
  const score = Math.min(4, Math.floor((passedRequirements.length / PASSWORD_REQUIREMENTS.length) * 4));

  let label = "Very Weak";
  let color = "text-red-500";

  switch (score) {
    case 1:
      label = "Weak";
      color = "text-red-400";
      break;
    case 2:
      label = "Fair";
      color = "text-yellow-500";
      break;
    case 3:
      label = "Good";
      color = "text-blue-500";
      break;
    case 4:
      label = "Strong";
      color = "text-green-500";
      break;
  }

  return {
    score,
    label,
    color,
    requirements: PASSWORD_REQUIREMENTS,
  };
};

export const getPasswordStrengthBarColor = (score: number): string => {
  switch (score) {
    case 0:
      return "bg-gray-200";
    case 1:
      return "bg-red-500";
    case 2:
      return "bg-yellow-500";
    case 3:
      return "bg-blue-500";
    case 4:
      return "bg-green-500";
    default:
      return "bg-gray-200";
  }
};

export const isPasswordValid = (password: string): boolean => {
  const requiredCount = 4; // Require at least 4 out of 6 requirements
  const passedCount = PASSWORD_REQUIREMENTS.filter((req) => req.test(password)).length;
  return passedCount >= requiredCount;
}; 